import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchUnitCommands } from '@sophocles/api/odlApp';

import {
  deleteCommandSchedule,
  fetchCommandSchedule,
  fetchCommandSchedules,
  patchCommandSchedule,
  postCommandSchedule,
} from '../../api/commandSchedule';

/**
 * Loads the odl-app command catalog for a site (GET /api/odl-app/sites/<siteId>/unit/commands/).
 * Caches the result in the commands slice keyed by site id. Short-circuits when
 * already cached or in-flight. Invalidate first with `invalidateAvailableCommands(siteId)`.
 */
export const loadUnitCommands = createAsyncThunk(
  'commands/odlAppSiteCommands/load',
  async (siteId, { rejectWithValue }) => {
    try {
      return await fetchUnitCommands(siteId);
    } catch (e) {
      return rejectWithValue(e?.response?.data ?? true);
    }
  },
  {
    condition: (siteId, { getState }) => {
      const entry = getState().commands?.availableCommands?.[siteId];
      return !(entry && (entry.loading || entry.data));
    },
  },
);

export const loadCommandSchedules = createAsyncThunk(
  'commands/schedules/loadMany',
  async (queryParams, { rejectWithValue }) => {
    try {
      const data = await fetchCommandSchedules(queryParams);
      return data;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const loadCommandSchedule = createAsyncThunk('commands/schedules/load', async (id, { rejectWithValue }) => {
  try {
    const data = await fetchCommandSchedule(id);
    return data;
  } catch (e) {
    return rejectWithValue(e.response.data);
  }
});

export const createCommandSchedule = createAsyncThunk(
  'commands/schedules/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await postCommandSchedule(data);
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const editCommandSchedule = createAsyncThunk('commands/schedules/edit', async (data, { rejectWithValue }) => {
  try {
    const { id, ...payload } = data;
    const response = await patchCommandSchedule(id, payload);
    return response;
  } catch (e) {
    return rejectWithValue(e.response.data);
  }
});

export const removeCommandSchedule = createAsyncThunk('commands/schedules/remove', async (id, { rejectWithValue }) => {
  try {
    await deleteCommandSchedule(id);
    return id;
  } catch (e) {
    return rejectWithValue(e.response.data);
  }
});
